/**
 * Admin "Login as user" — client half (v7.54). The mechanism lives in
 * server/middleware/impersonation.ts. This module:
 *   - captures ?impersonate=<token> on boot into sessionStorage (per-TAB, so
 *     the admin's own tab is never affected) and strips it from the URL,
 *   - patches window.fetch to send the token as a header on same-origin /api
 *     calls (515 call sites use fetch directly — a wrapper would miss most),
 *   - patches window.open so tabs/downloads spawned from the impersonated
 *     window inherit the token via the query param,
 *   - exposes withImpersonation(url) for <img src> URLs that can't carry headers,
 *   - ends the session: logs the impersonation session out (never the admin's)
 *     and closes the window.
 */

export const IMPERSONATION_HEADER = "x-admoji-impersonate";
export const IMPERSONATION_QUERY = "impersonate";
export const IMPERSONATION_ENDED_EVENT = "admoji:impersonation-ended";
const STORAGE_KEY = "admoji.impersonation.token";

let token: string | null = null;
// Why the token went away, kept so a banner that mounts AFTER the 401 (the App
// shows a spinner until /api/auth/me answers) can still show the ended card.
let endedReason: "expired" | "ended" | null = null;

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string | null): void {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, value);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — the in-memory token still covers this page */
  }
}

export function isImpersonating(): boolean {
  return token !== null;
}

export function getImpersonationToken(): string | null {
  return token;
}

/** "active" while a token is held; "ended"/"expired" after it went away; null in a normal tab. */
export function getImpersonationStatus(): "active" | "ended" | "expired" | null {
  return token ? "active" : endedReason;
}

function sameOrigin(url: string): URL | null {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin ? u : null;
  } catch {
    return null;
  }
}

/** Append the token to a same-origin URL — for <img src> and window.open targets. */
export function withImpersonation(url: string): string {
  if (!token || !url) return url;
  const u = sameOrigin(url);
  if (!u) return url;
  u.searchParams.set(IMPERSONATION_QUERY, token);
  return `${u.pathname}${u.search}${u.hash}`;
}

function requestUrl(input: RequestInfo | URL): string {
  return typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
}

function endedLocally(reason: "expired" | "ended"): void {
  if (token === null) return;
  token = null;
  endedReason = reason;
  writeStored(null);
  window.dispatchEvent(new CustomEvent(IMPERSONATION_ENDED_EVENT, { detail: { reason } }));
}

/** Call once, before React renders. */
export function bootstrapImpersonation(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get(IMPERSONATION_QUERY);
  if (fromUrl) {
    writeStored(fromUrl);
    params.delete(IMPERSONATION_QUERY);
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
    );
  }

  token = readStored();
  if (!token) return;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function impersonatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const current = token;
    const target = current ? sameOrigin(requestUrl(input)) : null;
    if (!current || !target || !target.pathname.startsWith("/api/")) return nativeFetch(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set(IMPERSONATION_HEADER, current);
    const res = await nativeFetch(input, { ...init, headers });
    if (res.status === 401) {
      try {
        const body = await res.clone().json();
        if (body?.code === "IMPERSONATION_ENDED") endedLocally("expired");
      } catch {
        /* not JSON */
      }
    }
    return res;
  };

  const nativeOpen = window.open.bind(window);
  window.open = function impersonatedOpen(url?: string | URL, target?: string, features?: string): Window | null {
    const str = url == null ? "" : String(url);
    return nativeOpen(str ? withImpersonation(str) : url, target, features);
  };
}

/** End the login-as session: server-side logout of the impersonation session only, then close. */
export async function endImpersonation(): Promise<void> {
  if (!token) return;
  try {
    // Goes through the patched fetch → destroys ONLY the impersonation session.
    await window.fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    /* best effort — the server session expires on its own */
  }
  endedLocally("ended");
  window.close();
}
